import { Friend, FriendRequest, InviteCode } from "../types";
import debugx from "debugx";
import { settingsService } from "./settingsService";
import { getMapDataSource } from "./mapDataSource";

const debug = debugx("bikedash:friends");

export class FriendsService {
  private friendsCallbacks: Set<(friends: Friend[]) => void> = new Set();
  private requestsCallbacks: Set<(requests: FriendRequest[]) => void> =
    new Set();
  private presenceInterval: number | null = null;
  private currentFriends: Friend[] = [];

  // Friend locations tracking
  private friendLocations: { [friendId: string]: any } = {};
  private locationCallbacks: Set<
    (locations: { [friendId: string]: any }) => void
  > = new Set();

  // Connection status tracking
  private connectionStatus: "online" | "offline" | "checking" | "degraded" =
    "online";
  private statusCallbacks: Set<
    (status: "online" | "offline" | "checking" | "degraded") => void
  > = new Set();
  private newRequestCallbacks: Set<(hasNew: boolean) => void> = new Set();
  private modalRefreshInterval: number | null = null;
  private backgroundCheckInterval: number | null = null;
  private lastRequestCount: number = 0;
  private lastSuccessfulCheck: number = Date.now();
  private lastStatusCheck: number = 0;
  private currentRequests: FriendRequest[] = [];
  private lastDismissedTimestamp: number = 0;
  private lastKnownRequestUpdate: number = 0;
  private lastKnownFriendsUpdate: number = 0;
  private lastError: string | null = null;
  private lastErrorTime: number = 0;

  // Helper to make authenticated API calls (uses cookies)
  // Returns Response on success, null on error (sets degraded status)
  private async authenticatedFetch(
    url: string,
    options: RequestInit = {},
  ): Promise<Response | null> {
    try {
      const response = await fetch(url, {
        ...options,
        credentials: "include", // Include cookies for authentication
        headers: {
          "Content-Type": "application/json",
          ...options.headers,
        },
      });

      if (!response.ok) {
        // Try to parse error message from response
        let errorMessage: string | null = null;
        try {
          const errorData = await response.json();
          if (errorData.error) {
            errorMessage = errorData.error;
          }
        } catch {
          // JSON parsing failed, will use fallback
        }

        // If we couldn't parse an error message, use friendly fallbacks
        if (!errorMessage) {
          if (response.status >= 500) {
            errorMessage =
              "Server is experiencing issues. Please try again later.";
          } else if (response.status === 401 || response.status === 403) {
            errorMessage = "Authentication required. Please sign in again.";
          } else if (response.status === 404) {
            errorMessage = "Resource not found";
          } else {
            // Final fallback for any other unexpected status
            errorMessage = "Something went wrong. Please try again.";
          }
        }

        debug.error(
          "Request error for %s: %d - %s",
          url,
          response.status,
          errorMessage,
        );

        // Track error and set degraded status
        this.lastError = errorMessage;
        this.lastErrorTime = Date.now();
        this.setConnectionStatus("degraded");

        return null;
      }

      // Success - clear degraded status if we were degraded
      if (this.connectionStatus === "degraded") {
        this.setConnectionStatus("online");
        this.lastError = null;
      }

      return response;
    } catch (error) {
      // Network errors, timeouts, or other fetch failures
      let errorMessage = "Network request failed";
      if (error instanceof Error) {
        errorMessage = error.message;
        debug.error("Fetch error for %s: %s", url, error.message);
      } else {
        debug.error("Unknown fetch error for %s", url);
      }

      // Track error and set degraded status
      this.lastError = errorMessage;
      this.lastErrorTime = Date.now();
      this.setConnectionStatus("degraded");

      return null;
    }
  }

  // ============================================================================
  // Friends Management
  // ============================================================================

  // Get cached friends synchronously (for use in subscriptions)
  getCachedFriends(): Friend[] {
    return this.currentFriends;
  }

  // Get cached friend locations synchronously
  getCachedFriendLocations(): { [friendId: string]: any } {
    return this.friendLocations;
  }

  async getFriends(): Promise<Friend[]> {
    const response = await this.authenticatedFetch("/api/auth/friends");
    if (!response) {
      debug.warn("Failed to get friends - keeping cached data");
      return this.currentFriends;
    }

    const friends = await response.json();
    debug("Loaded %d friends", friends.length);

    // Only update if data is valid (avoid flickering from empty responses)
    if (Array.isArray(friends)) {
      // Don't notify with empty data if we already have cached data
      // This prevents flickering when API temporarily returns empty
      if (friends.length > 0 || this.currentFriends.length === 0) {
        // Preserve online status from cache when updating friends list
        const mergedFriends = friends.map((friend) => {
          const cached = this.currentFriends.find((f) => f.id === friend.id);
          if (cached && cached.status === "online") {
            return { ...friend, status: "online" };
          }
          return friend;
        });

        this.currentFriends = mergedFriends;
        this.notifyFriendsChange(mergedFriends);
      } else {
        debug.warn(
          "Ignoring empty friends response - keeping cached data (%d friends)",
          this.currentFriends.length,
        );
      }
    } else {
      debug.warn("Invalid friends response:", friends);
    }

    return friends;
  }

  async sendFriendRequest(params: {
    email?: string;
    username?: string;
    inviteCode?: string;
  }): Promise<{
    requestId?: string;
    status?: string;
    pending?: boolean;
    message?: string;
    success?: boolean;
  }> {
    const response = await this.authenticatedFetch(
      "/api/auth/friends/request",
      {
        method: "POST",
        body: JSON.stringify(params),
      },
    );

    if (!response) {
      return { success: false, message: "Failed to send friend request" };
    }

    const result = await response.json();
    debug("Friend request sent:", result);
    return result;
  }

  async acceptFriendRequest(
    requestId: string,
    recipientSettings?: {
      shareActivities: boolean;
      shareLocation: boolean;
      shareStatus: boolean;
      shareRoutes: boolean;
    },
  ): Promise<void> {
    const response = await this.authenticatedFetch(
      `/api/auth/friends/accept/${requestId}`,
      {
        method: "POST",
        body: JSON.stringify(recipientSettings || {}),
      },
    );

    if (!response) {
      debug.warn("Failed to accept friend request");
      return;
    }

    debug("Accepted friend request:", requestId);

    // Refresh friends and requests lists
    await Promise.all([this.getFriends(), this.getFriendRequests()]);
  }

  async declineFriendRequest(requestId: string): Promise<void> {
    const response = await this.authenticatedFetch(
      `/api/auth/friends/decline/${requestId}`,
      {
        method: "POST",
      },
    );

    if (!response) {
      debug.warn("Failed to decline friend request");
      return;
    }

    debug("Declined friend request:", requestId);

    // Refresh requests list
    await this.getFriendRequests();
  }

  async removeFriend(friendshipId: string): Promise<void> {
    const response = await this.authenticatedFetch(
      `/api/auth/friends/${friendshipId}`,
      {
        method: "DELETE",
      },
    );

    if (!response) {
      debug.warn("Failed to remove friend");
      return;
    }

    debug("Removed friend:", friendshipId);

    // Refresh friends list
    await this.getFriends();
  }

  async updateFriendRequestSettings(
    requestId: string,
    settings: {
      shareActivities?: boolean;
      shareLocation?: boolean;
      shareStatus?: boolean;
      shareRoutes?: boolean;
    },
  ): Promise<void> {
    const response = await this.authenticatedFetch(
      `/api/auth/friends/request/${requestId}`,
      {
        method: "PUT",
        body: JSON.stringify(settings),
      },
    );

    if (!response) {
      debug.warn("Failed to update friend request settings");
      return;
    }

    debug("Updated friend request settings:", requestId, settings);
  }

  async updateFriendSettings(
    friendshipId: string,
    settings: {
      shareActivities?: boolean;
      shareLocation?: boolean;
      shareMode?: "always" | "proximity" | "activity" | "manual";
      proximityRadius?: number;
      shareStatus?: boolean;
      shareRoutes?: boolean;
      mapIcon?: string;
      mapColor?: string;
    },
  ): Promise<void> {
    const response = await this.authenticatedFetch(
      `/api/auth/friends/${friendshipId}`,
      {
        method: "PUT",
        body: JSON.stringify(settings),
      },
    );

    if (!response) {
      debug.warn("Failed to update friend settings");
      return;
    }

    debug("Updated friend settings:", friendshipId, settings);

    // Refresh friends list
    await this.getFriends();
  }

  async getFriendRequests(): Promise<FriendRequest[]> {
    const response = await this.authenticatedFetch(
      "/api/auth/friends/requests",
    );

    if (!response) {
      debug.warn("Failed to get friend requests");
      return [];
    }

    const requests = await response.json();
    debug("Loaded %d friend requests", requests.length);

    // Only update if data is valid (avoid flickering from empty responses)
    if (Array.isArray(requests)) {
      this.notifyRequestsChange(requests);
    } else {
      debug.warn("Invalid friend requests response:", requests);
    }

    return requests;
  }

  // ============================================================================
  // Invite Codes
  // ============================================================================

  async generateInviteCode(): Promise<InviteCode | null> {
    const response = await this.authenticatedFetch("/api/auth/invite-code", {
      method: "POST",
    });

    if (!response) {
      debug.warn("Failed to generate invite code");
      return null;
    }

    const result = await response.json();
    debug("Generated invite code:", result.code);

    return {
      id: result.id || `invite_${Date.now()}`,
      code: result.code,
      createdAt: result.createdAt || Date.now(),
      expiresAt: result.expiresAt,
      maxUses: null,
      useCount: result.useCount || 0,
      usedBy: result.usedBy || [],
      isActive: true,
    };
  }

  async getMyInviteCode(): Promise<InviteCode | null> {
    const response = await this.authenticatedFetch("/api/auth/invite-code");

    if (!response) {
      debug.warn("Failed to get invite code");
      return null;
    }

    const result = await response.json();

    if (!result.code) {
      return null;
    }

    debug("Fetched invite code:", result.code);

    return {
      id: result.id || `invite_${Date.now()}`,
      code: result.code,
      createdAt: result.createdAt || Date.now(),
      expiresAt: result.expiresAt,
      maxUses: null,
      useCount: result.useCount || 0,
      usedBy: result.usedBy || [],
      isActive: true,
    };
  }

  async useInviteCode(code: string): Promise<{ username: string } | null> {
    const response = await this.authenticatedFetch(
      "/api/auth/invite-code/use",
      {
        method: "POST",
        body: JSON.stringify({ code }),
      },
    );

    if (!response) {
      debug.warn("Failed to validate invite code");
      return null;
    }

    const result = await response.json();
    debug("Validated invite code:", code);

    // Just return the code owner info - don't refresh lists yet
    // The friend request will be sent separately
    return {
      username: result.codeOwner?.username || "Unknown",
    };
  }

  // ============================================================================
  // Presence & Status
  // ============================================================================

  async updateMyStatus(
    status: "online" | "offline" | "invisible",
  ): Promise<void> {
    const response = await this.authenticatedFetch("/api/auth/status", {
      method: "PUT",
      body: JSON.stringify({ status }),
    });

    if (!response) {
      debug.warn("Failed to update status");
      return;
    }

    debug("Updated status to: %s", status);
  }

  async getFriendsPresence(): Promise<Friend[]> {
    const response = await this.authenticatedFetch(
      "/api/auth/friends/presence",
    );

    if (!response) {
      debug.warn("Failed to get friends presence - keeping cached data");
      return this.currentFriends;
    }

    const friends = await response.json();
    debug("Got presence for %d friends", friends.length);

    // Only update if data is valid (avoid flickering from empty responses)
    if (Array.isArray(friends)) {
      // Don't notify with empty data if we already have cached data
      // This prevents flickering when API temporarily returns empty
      if (friends.length > 0 || this.currentFriends.length === 0) {
        this.currentFriends = friends;
        this.notifyFriendsChange(friends);
      } else {
        debug.warn(
          "Ignoring empty presence response - keeping cached data (%d friends)",
          this.currentFriends.length,
        );
      }
    } else {
      debug.warn("Invalid presence response:", friends);
    }

    return friends;
  }

  async sendPresencePing(): Promise<void> {
    // Check if location sharing is enabled
    const shareLocation =
      settingsService.getUserPreferences().shareLocationWithFriends;

    let body: any = {};

    if (shareLocation) {
      // Get current position from mapDataSource if available
      const mapDataSource = getMapDataSource();
      if (mapDataSource?.currentPosition) {
        const pos = mapDataSource.currentPosition;

        // Only send if we have valid lat/lng
        if (pos.lat !== undefined && pos.lng !== undefined) {
          body.location = {
            lat: pos.lat,
            lng: pos.lng,
          };

          // Add heading if available (prefer compassHeading, fallback to GPS heading)
          if (pos.compassHeading !== undefined) {
            body.heading = pos.compassHeading;
          } else if (pos.heading !== undefined) {
            body.heading = pos.heading;
          }

          // Add speed in km/h if available
          if (pos.speed !== undefined) {
            // Convert m/s to km/h for consistency
            body.speed = pos.speed * 3.6;
          }

          debug(
            "Sending presence with location: lat=%s, lng=%s, heading=%s, speed=%s km/h",
            pos.lat.toFixed(6),
            pos.lng.toFixed(6),
            body.heading?.toFixed(1),
            body.speed?.toFixed(1),
          );
        }
      }
    }

    const response = await this.authenticatedFetch("/api/auth/presence/ping", {
      method: "POST",
      body: Object.keys(body).length > 0 ? JSON.stringify(body) : undefined,
    });

    if (!response) {
      debug.warn("Failed to send presence ping");
      return;
    }

    debug("Sent presence ping");
  }

  startPresenceUpdates(): void {
    if (this.presenceInterval) return;

    // Use regular getFriends instead of getFriendsPresence
    // The presence endpoint is too expensive and unreliable
    this.getFriends();

    // Send initial ping
    this.sendPresencePing();

    // Check every 30 seconds - use regular friends endpoint
    this.presenceInterval = window.setInterval(() => {
      this.getFriends(); // Use regular endpoint instead of presence
      this.sendPresencePing();
    }, 30000);

    debug("Started presence updates (30s interval)");
  }

  stopPresenceUpdates(): void {
    if (this.presenceInterval) {
      clearInterval(this.presenceInterval);
      this.presenceInterval = null;
      debug("Stopped presence updates");
    }
  }

  // ============================================================================
  // Connection Status & Auto-refresh
  // ============================================================================

  getConnectionStatus(): "online" | "offline" | "checking" | "degraded" {
    return this.connectionStatus;
  }

  getLastSuccessfulCheck(): number {
    return this.lastSuccessfulCheck;
  }

  getLastError(): { message: string; timestamp: number } | null {
    if (this.lastError && this.lastErrorTime) {
      return { message: this.lastError, timestamp: this.lastErrorTime };
    }
    return null;
  }

  private setConnectionStatus(
    status: "online" | "offline" | "checking" | "degraded",
  ) {
    if (this.connectionStatus !== status) {
      this.connectionStatus = status;
      debug("Connection status changed to: %s", status);
      this.notifyStatusChange(status);
    }
  }

  async checkConnection(): Promise<boolean> {
    try {
      this.setConnectionStatus("checking");

      // Use lightweight status endpoint for health check
      const response = await fetch("/api/auth/friends/status", {
        method: "GET",
        credentials: "include",
        cache: "no-cache",
        signal: AbortSignal.timeout(5000),
      });

      const isHealthy = response.ok;
      this.setConnectionStatus(isHealthy ? "online" : "offline");

      if (isHealthy) {
        this.lastSuccessfulCheck = Date.now();
      }

      return isHealthy;
    } catch (error) {
      debug.error("Connection check failed:", error);
      this.setConnectionStatus("offline");
      return false;
    }
  }

  async checkFriendsStatus(): Promise<{
    lastRequestUpdate: number;
    lastFriendsUpdate: number;
    pendingRequestCount: number;
    lastUpdate: number;
    onlineFriends: { [friendId: string]: string };
    friendLocations?: { [friendId: string]: any };
  } | null> {
    try {
      // Check if location sharing is enabled
      const shareLocation =
        settingsService.getUserPreferences().shareLocationWithFriends;

      let bodyData: any = null;

      if (shareLocation) {
        // Get current position from mapDataSource if available
        const mapDataSource = getMapDataSource();
        if (mapDataSource?.currentPosition) {
          const pos = mapDataSource.currentPosition;

          // Only send if we have valid lat/lng
          if (pos.lat !== undefined && pos.lng !== undefined) {
            bodyData = {
              location: {
                lat: pos.lat,
                lng: pos.lng,
              },
            };

            // Add heading if available (prefer compassHeading, fallback to GPS heading)
            if (pos.compassHeading !== undefined) {
              bodyData.heading = pos.compassHeading;
            } else if (pos.heading !== undefined) {
              bodyData.heading = pos.heading;
            }

            // Add speed in km/h if available
            if (pos.speed !== undefined) {
              // Convert m/s to km/h for consistency
              bodyData.speed = pos.speed * 3.6;
            }

            debug(
              "Sending location with status check: lat=%s, lng=%s, heading=%s, speed=%s km/h",
              pos.lat.toFixed(6),
              pos.lng.toFixed(6),
              bodyData.heading?.toFixed(1),
              bodyData.speed?.toFixed(1),
            );
          }
        }
      } else {
        // If location sharing is OFF, send empty location to clear it on server
        bodyData = {
          location: null,
        };
        debug("Sending null location to clear server-side location data");
      }

      const response = await fetch("/api/auth/friends/status", {
        method: bodyData ? "POST" : "GET",
        credentials: "include",
        cache: "no-cache",
        headers: bodyData ? { "Content-Type": "application/json" } : undefined,
        body: bodyData ? JSON.stringify(bodyData) : undefined,
      });

      if (!response.ok) {
        return null;
      }

      const status = await response.json();

      // Update friend locations if provided
      if (status.friendLocations) {
        this.friendLocations = status.friendLocations;
        debug(
          "Updated friend locations: %d friends with location data",
          Object.keys(status.friendLocations).length,
        );
        debug("Friend locations data: %o", status.friendLocations);
        this.notifyLocationChange(status.friendLocations);
      } else if (Object.keys(this.friendLocations).length > 0) {
        // Clear locations if server no longer sends any
        this.friendLocations = {};
        this.notifyLocationChange({});
        debug("Cleared friend locations (no data from server)");
      }

      return status;
    } catch (error) {
      debug.error("Failed to check friends status:", error);
      return null;
    }
  }

  startModalRefresh(): void {
    if (this.modalRefreshInterval) return;

    debug("Starting modal refresh (5s interval)");

    // Initial refresh
    Promise.all([this.getFriends(), this.getFriendRequests()]).catch((error) =>
      debug.error("Initial modal refresh failed:", error),
    );

    // Refresh every 5 seconds while modal is open
    this.modalRefreshInterval = window.setInterval(() => {
      Promise.all([this.getFriends(), this.getFriendRequests()]).catch(
        (error) => debug.error("Modal refresh failed:", error),
      );
    }, 5000);
  }

  stopModalRefresh(): void {
    if (this.modalRefreshInterval) {
      clearInterval(this.modalRefreshInterval);
      this.modalRefreshInterval = null;
      debug("Stopped modal refresh");
    }
  }

  startBackgroundChecks(): void {
    if (this.backgroundCheckInterval) return;

    debug("Starting background checks (1s interval, dynamic rate)");

    // Load friends list initially if not already loaded
    if (this.currentFriends.length === 0) {
      debug("Loading friends list on startup");
      this.getFriends().catch((error) =>
        debug.error("Failed to load friends on startup:", error),
      );
    }

    // Initial check
    this.checkConnection();
    this.checkForNewRequestsLightweight();

    // Check every 1 second, but adjust rate based on location sharing
    this.backgroundCheckInterval = window.setInterval(() => {
      const now = Date.now();
      const shareLocation =
        settingsService.getUserPreferences().shareLocationWithFriends;

      // If location sharing is on, check every 1 second
      // If location sharing is off, check every 10 seconds
      const interval = shareLocation ? 1000 : 10000;

      if (now - this.lastStatusCheck >= interval) {
        this.lastStatusCheck = now;
        this.checkConnection();
        this.checkForNewRequestsLightweight();
      }
    }, 1000);
  }

  stopBackgroundChecks(): void {
    if (this.backgroundCheckInterval) {
      clearInterval(this.backgroundCheckInterval);
      this.backgroundCheckInterval = null;
      debug("Stopped background checks");
    }
  }

  // Lightweight check using status endpoint - minimal data transfer
  private async checkForNewRequestsLightweight(): Promise<void> {
    try {
      const status = await this.checkFriendsStatus();
      if (!status) {
        debug.error("Failed to get friends status");
        return;
      }

      // Update online status for friends in cache
      if (status.onlineFriends && this.currentFriends.length > 0) {
        const updatedFriends = this.currentFriends.map((friend) => {
          const onlineStatus = status.onlineFriends[friend.id] as
            | "online"
            | "offline"
            | "riding"
            | undefined;
          if (onlineStatus) {
            return { ...friend, status: onlineStatus };
          } else if (friend.status === "online") {
            // Friend was online but is no longer - mark as offline
            return { ...friend, status: "offline" as const };
          }
          return friend;
        });

        // Check if any status actually changed
        const statusChanged = updatedFriends.some(
          (f, i) => f.status !== this.currentFriends[i].status,
        );

        if (statusChanged) {
          debug("Friend online status updated");
          this.currentFriends = updatedFriends;
          this.notifyFriendsChange(updatedFriends);
        }
      }

      // Detect if anything changed since last check
      const requestsChanged =
        status.lastRequestUpdate > this.lastKnownRequestUpdate;
      const friendsChanged =
        status.lastFriendsUpdate > this.lastKnownFriendsUpdate;

      // If anything changed, trigger full data refresh
      if (requestsChanged || friendsChanged) {
        debug(
          "Changes detected - requests: %s, friends: %s - fetching full data",
          requestsChanged,
          friendsChanged,
        );

        // Fetch full data in background
        if (requestsChanged) {
          this.getFriendRequests().catch((error) =>
            debug.error("Failed to fetch updated requests:", error),
          );
        }
        if (friendsChanged) {
          this.getFriends().catch((error) =>
            debug.error("Failed to fetch updated friends:", error),
          );
        }
      }

      // If no pending requests, clear notification
      if (status.pendingRequestCount === 0) {
        this.notifyNewRequests(false);
        this.lastRequestCount = 0;
        this.lastKnownRequestUpdate = status.lastRequestUpdate;
        this.lastKnownFriendsUpdate = status.lastFriendsUpdate;
        return;
      }

      // Check if there are new/updated requests since last dismiss
      if (status.lastRequestUpdate > this.lastDismissedTimestamp) {
        debug(
          "New friend requests detected - last update: %d, last dismissed: %d, count: %d",
          status.lastRequestUpdate,
          this.lastDismissedTimestamp,
          status.pendingRequestCount,
        );
        this.notifyNewRequests(true);
      } else {
        // Requests exist but all were before dismiss time
        this.notifyNewRequests(false);
      }

      this.lastRequestCount = status.pendingRequestCount;
      this.lastKnownRequestUpdate = status.lastRequestUpdate;
      this.lastKnownFriendsUpdate = status.lastFriendsUpdate;
    } catch (error) {
      debug.error("Failed to check for new requests:", error);
    }
  }

  // Full check - fetches all request data (used by modal refresh)
  private async checkForNewRequests(): Promise<void> {
    try {
      const requests = await this.getFriendRequests();
      const incomingRequests = requests.filter(
        (r) => r.direction === "incoming",
      );

      // If no incoming requests, clear the notification
      if (incomingRequests.length === 0) {
        this.notifyNewRequests(false);
        this.lastRequestCount = 0;
        return;
      }

      // Get the most recent request timestamp
      const mostRecentTimestamp = Math.max(
        ...incomingRequests.map((r) => r.createdAt),
      );

      // Compare with last dismissed timestamp (in-memory only)
      // If user dismissed, only show again if new requests arrive after that
      if (mostRecentTimestamp > this.lastDismissedTimestamp) {
        debug(
          "New friend requests detected - most recent: %d, last dismissed: %d",
          mostRecentTimestamp,
          this.lastDismissedTimestamp,
        );
        this.notifyNewRequests(true);
      } else {
        // Requests exist but all were before dismiss time
        this.notifyNewRequests(false);
      }

      this.lastRequestCount = incomingRequests.length;
    } catch (error) {
      debug.error("Failed to check for new requests:", error);
    }
  }

  dismissNewRequests(): void {
    this.lastDismissedTimestamp = Date.now();
    debug("Dismissed friend requests at: %d", this.lastDismissedTimestamp);
  }

  // ============================================================================
  // Subscribe to changes
  // ============================================================================

  onFriendsChange(callback: (friends: Friend[]) => void): () => void {
    this.friendsCallbacks.add(callback);
    return () => {
      this.friendsCallbacks.delete(callback);
    };
  }

  onRequestsChange(callback: (requests: FriendRequest[]) => void): () => void {
    this.requestsCallbacks.add(callback);
    return () => {
      this.requestsCallbacks.delete(callback);
    };
  }

  onStatusChange(
    callback: (status: "online" | "offline" | "checking" | "degraded") => void,
  ): () => void {
    this.statusCallbacks.add(callback);
    return () => {
      this.statusCallbacks.delete(callback);
    };
  }

  onNewRequests(callback: (hasNew: boolean) => void): () => void {
    this.newRequestCallbacks.add(callback);
    return () => {
      this.newRequestCallbacks.delete(callback);
    };
  }

  onFriendLocationsChange(
    callback: (locations: { [friendId: string]: any }) => void,
  ): () => void {
    this.locationCallbacks.add(callback);
    // Immediately notify with current locations
    callback(this.friendLocations);
    return () => {
      this.locationCallbacks.delete(callback);
    };
  }

  private notifyFriendsChange(friends: Friend[]) {
    this.friendsCallbacks.forEach((callback) => callback(friends));
  }

  private notifyRequestsChange(requests: FriendRequest[]) {
    this.requestsCallbacks.forEach((callback) => callback(requests));
  }

  private notifyStatusChange(
    status: "online" | "offline" | "checking" | "degraded",
  ) {
    this.statusCallbacks.forEach((callback) => callback(status));
  }

  private notifyNewRequests(hasNew: boolean) {
    this.newRequestCallbacks.forEach((callback) => callback(hasNew));
  }

  private notifyLocationChange(locations: { [friendId: string]: any }) {
    this.locationCallbacks.forEach((callback) => callback(locations));
  }
}

export const friendsService = new FriendsService();
